/**
 * Global axios configuration.
 *
 * Two responsibilities:
 *   1. Set a 15-second default timeout on all axios calls. Previously
 *      requests could hang indefinitely on flaky venue Wi-Fi, freezing UI
 *      spinners forever (the "loading forever" complaint from clients).
 *      15 s is comfortably above p99 latency for our slowest endpoint
 *      (Apple catalog resolve, ~4 s) but short enough that a real
 *      connection loss surfaces as an error the UI can react to.
 *
 *   2. Emit a global `network-status` event whenever a request finishes,
 *      so the Connectivity Pill (see components/ConnectivityPill.js) can
 *      show "reconnecting…" the moment we observe a failure — much faster
 *      than waiting for the browser's `offline` event, which only fires
 *      when the OS declares the whole interface down (Chrome on Android
 *      won't fire it during a DNS blip mid-set).
 *
 * Call `installAxiosDefaults()` exactly once, at app boot, before any
 * axios usage.
 */
import axios from 'axios';

const NETWORK_EVENT = 'app:network-status';
let installed = false;

// ---------------------------------------------------------------------------
// Silent token-refresh (Feb 2026)
//
// Access-token cookies expire after 24h. Long shifts (>24h with the tablet
// on the same tab) previously punted the operator to /login mid-set. Now:
//   • On any 401 (except from /auth/* itself), fire ONE /api/auth/refresh
//     using the httponly refresh cookie (7-day TTL, backend already
//     rotates both cookies on success).
//   • Retry the original request exactly once.
//   • If refresh itself 401s, give up — the operator's refresh cookie
//     really has expired; surface the original 401 to the caller.
//
// De-dupe concurrent 401s: if 20 requests fail simultaneously we do ONE
// refresh, everyone waits for it, then all replay. Prevents refresh
// storms that would rotate the refresh token 20 times in a row (harmless
// but noisy in logs).
// ---------------------------------------------------------------------------
let refreshInFlight = null;
const REFRESH_PATH = '/api/auth/refresh';
const GOOGLE_REFRESH_PATH = '/api/google-auth/refresh';
// Never try to refresh in response to a failure of these paths — an
// infinite loop of `refresh → 401 → refresh` would otherwise be possible
// on servers that require auth to even reach /refresh.
const REFRESH_SKIP_PATHS = [
  '/api/auth/refresh', '/api/auth/login', '/api/auth/register', '/api/auth/logout',
  '/api/google-auth/refresh', '/api/google-auth/session', '/api/google-auth/logout',
];

function shouldAttemptRefresh(err) {
  const cfg = err?.config || {};
  const status = err?.response?.status;
  if (status !== 401) return false;
  const url = cfg.url || '';
  if (REFRESH_SKIP_PATHS.some((p) => url.includes(p))) return false;
  return !cfg.__retriedAfterRefresh;
}

async function performRefresh() {
  if (!refreshInFlight) {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Bare fetch so we can't recurse into our own interceptor mid-refresh.
    // The session may be EITHER a legacy JWT (access/refresh cookies) OR a
    // Google session (session_token cookie) — they have different refresh
    // endpoints. Try the JWT rolling refresh first; if there's no refresh
    // cookie (Google booth), fall back to the Google sliding renewal. The
    // irrelevant one 401s harmlessly. Either success keeps the booth alive.
    const tryOne = (path) => fetch(`${backendUrl}${path}`, { method: 'POST', credentials: 'include' })
      .then((r) => r.ok).catch(() => false);
    refreshInFlight = (async () => {
      if (await tryOne(REFRESH_PATH)) return true;
      return tryOne(GOOGLE_REFRESH_PATH);
    })().finally(() => {
      // Clear the shared promise a tick later so any 401s racing right
      // now still join THIS refresh instead of firing a second one.
      setTimeout(() => { refreshInFlight = null; }, 50);
    });
  }
  return refreshInFlight;
}

// ---------------------------------------------------------------------------
// PROACTIVE keep-alive (Jun 2026) — the reactive refresh above only fires
// AFTER a 401, so at every token-expiry boundary one request fails first, and
// on an always-on venue tablet that failing request can land on the AI booth's
// "fetch next track" call at a crossfade boundary (RCA: Oct-2026 stall). This
// refreshes the session LONG before it can expire so a boundary 401 never
// happens. Access JWT TTL is 24h and the Google session is 7d; a 12h cadence
// (plus visibility/online/focus catch-ups) keeps both perpetually fresh even
// if a backgrounded-tab timer gets throttled. Cookies are httpOnly (no client
// read of expiry), so we refresh on a safe fixed cadence rather than on exp.
// ---------------------------------------------------------------------------
const PROACTIVE_REFRESH_MS = 12 * 60 * 60 * 1000;   // every 12h
const MIN_REFRESH_GAP_MS = 60 * 60 * 1000;          // event-driven refreshes at most hourly
let lastRefreshAt = 0;
let proactiveTimer = null;

async function proactiveRefresh() {
  if (Date.now() - lastRefreshAt < MIN_REFRESH_GAP_MS) return;
  lastRefreshAt = Date.now();          // mark up-front so overlapping events don't double-fire
  const ok = await performRefresh();
  if (!ok) lastRefreshAt = 0;          // let the next event retry promptly if it failed
}

function onWake() {
  if (typeof document === 'undefined' || document.visibilityState === 'visible') proactiveRefresh();
}

export function startProactiveRefresh() {
  if (proactiveTimer || typeof window === 'undefined') return;
  lastRefreshAt = 0;                   // allow the first kick immediately on login/boot
  proactiveRefresh();                  // fresh cookies right away for a near-expiry tab
  proactiveTimer = setInterval(proactiveRefresh, PROACTIVE_REFRESH_MS);
  document.addEventListener('visibilitychange', onWake);
  window.addEventListener('focus', onWake);
  window.addEventListener('online', onWake);
}

export function stopProactiveRefresh() {
  if (proactiveTimer) { clearInterval(proactiveTimer); proactiveTimer = null; }
  if (typeof window !== 'undefined') {
    document.removeEventListener('visibilitychange', onWake);
    window.removeEventListener('focus', onWake);
    window.removeEventListener('online', onWake);
  }
  lastRefreshAt = 0;
}

/**
 * Broadcast a state to any Connectivity listeners.
 * status: 'online' | 'offline' | 'reconnecting'
 */
function emit(status, detail = {}) {
  try {
    window.dispatchEvent(new CustomEvent(NETWORK_EVENT, { detail: { status, at: Date.now(), ...detail } }));
  } catch (_) { /* noop — SSR or event constructor blocked */ }
}

export function installAxiosDefaults() {
  if (installed) return;
  installed = true;

  // 15 s default. Individual calls that need longer (uploads, e.g.) can
  // still override with `{ timeout: 60000 }`.
  axios.defaults.timeout = 15000;
  // Cookies are our primary auth vehicle — must be sent cross-site for
  // the preview.emergentagent.com → api.emergentagent.com hop.
  axios.defaults.withCredentials = true;

  axios.interceptors.response.use(
    (res) => {
      emit('online');
      return res;
    },
    async (err) => {
      // Distinguish "server responded with an error" from "no response at
      // all". The pill only cares about the latter.
      const noResponse = !err?.response;
      const isTimeout = err?.code === 'ECONNABORTED';
      if (noResponse || isTimeout) {
        emit('reconnecting', {
          url: err?.config?.url,
          reason: isTimeout ? 'timeout' : (err?.message || 'network'),
        });
      }
      // Silent-refresh + one-shot replay on 401.
      if (shouldAttemptRefresh(err)) {
        const ok = await performRefresh();
        if (ok) {
          const cfg = err.config;
          cfg.__retriedAfterRefresh = true;
          return axios(cfg);
        }
      }
      return Promise.reject(err);
    }
  );

  // OS-level events — cheap and free.
  window.addEventListener('online', () => emit('online'));
  window.addEventListener('offline', () => emit('offline'));
}

export const NETWORK_STATUS_EVENT = NETWORK_EVENT;
